import React, { useRef, useEffect, useState } from 'react';
import { ExpertCard } from './ExpertCard';
import type { Expert } from './expert.type';

interface ExpertCarouselProps {
  title: string;
  experts: Expert[];
  isCarousel?: boolean;
  autoPlay?: boolean;
  renderCard?: (expert: Expert) => React.ReactNode; 
}

export const ExpertCarousel: React.FC<ExpertCarouselProps> = ({ 
  title, 
  experts, 
  isCarousel = false,
  autoPlay = false,
  renderCard 
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // 🟢 1. STATE: Tracks if the user wants the Grid view (Expanded) or Carousel view
  const [isExpanded, setIsExpanded] = useState(!isCarousel);

  const handleNext = () => {
    if (scrollContainerRef.current && scrollContainerRef.current.children.length > 0) {
      const container = scrollContainerRef.current;
      const itemWidth = container.children[0].getBoundingClientRect().width + 24; 
      
      if (container.scrollLeft + container.clientWidth >= container.scrollWidth - 10) {
        container.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        container.scrollBy({ left: itemWidth, behavior: 'smooth' });
      }
    }
  };

  const handlePrev = () => {
    if (scrollContainerRef.current && scrollContainerRef.current.children.length > 0) {
      const container = scrollContainerRef.current;
      const itemWidth = container.children[0].getBoundingClientRect().width + 24;
      container.scrollBy({ left: -itemWidth, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    // 🟢 Don't auto-play if we are in the expanded grid view
    if (isExpanded || !autoPlay || experts.length <= 1) return;
    
    const timer = setInterval(() => {
      handleNext();
    }, 3000);

    return () => clearInterval(timer);
  }, [isExpanded, autoPlay, experts.length]);

  return (
    <div className="mb-12 border-b border-gray-100 pb-8 last:border-0 relative">
      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800 pl-3 border-l-4 border-blue-600">{title}</h2>
        
        {/* 🟢 2. TOGGLE BUTTON: Changes text and flips the arrow! */}
        <button 
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-blue-600 font-semibold text-sm hover:bg-blue-50 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1"
        >
          {isExpanded ? 'View Less' : 'View More'}
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className={`h-4 w-4 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} 
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      <div className="relative group/carousel">
        {/* Only show Prev arrow if we are NOT expanded */}
        {!isExpanded && experts.length > 1 && (
          <button onClick={handlePrev} className="hidden md:block absolute -left-6 top-1/2 -translate-y-1/2 z-20 p-3 bg-white rounded-full shadow-[0_4px_12px_rgba(0,0,0,0.1)] border border-gray-100 hover:bg-blue-50 hover:text-blue-600 text-gray-500 transition-all opacity-0 group-hover/carousel:opacity-100 focus:opacity-100" title="Previous">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /></svg>
          </button>
        )}

        {/* 🟢 3. THE SWAP: If !isExpanded -> Carousel. If isExpanded -> Original Grid! */}
        {!isExpanded ? (
          <div ref={scrollContainerRef} className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth hide-scrollbar pb-4 pt-1">
            {experts.map((expert) => (
              <div key={expert.expertCode} className="snap-start shrink-0 w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] xl:w-[calc(25%-18px)]">
                {renderCard ? renderCard(expert) : <ExpertCard data={expert} />}
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pt-1">
            {experts.map((expert) => (
              <React.Fragment key={expert.expertCode}>
                {renderCard ? renderCard(expert) : <ExpertCard data={expert} />}
              </React.Fragment>
            ))}
          </div>
        )}

        {experts.length === 0 && (
          <div className="col-span-full py-8 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
            <p className="text-gray-400 text-sm italic">No {title.toLowerCase()} available at the moment.</p>
          </div>
        )}

        {/* Only show Next arrow if we are NOT expanded */}
        {!isExpanded && experts.length > 1 && (
          <button onClick={handleNext} className="hidden md:block absolute -right-6 top-1/2 -translate-y-1/2 z-20 p-3 bg-white rounded-full shadow-[0_4px_12px_rgba(0,0,0,0.1)] border border-gray-100 hover:bg-blue-50 hover:text-blue-600 text-gray-500 transition-all opacity-0 group-hover/carousel:opacity-100 focus:opacity-100" title="Next">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
          </button>
        )}
      </div>
    </div>
  );
};